import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Drawer } from "vaul";
import { Loader, Save, Trash2, X } from "lucide-react";
import Spacing from "../Spacing";
import { notify, getPractice } from "../../helpers";
import { updateExIpa } from "../../services/exIpa";
import { fetchAllUsers } from "../../services/users";
import MultiSelectWithSearch from "../MultiSelectWithSearch";

export default function ExIpaEditModal({
  data = {},
  reloadData = () => { },
  onClose = () => { },
  previousScrollPosition = 0,
}) {
  const dropdownRef = useRef(null);

  const account_id = getPractice("account_id");
  const location_id = getPractice("location_id");

  const [ipaName, setIpaName] = useState("");
  const [userIds, setUserIds] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIpaName(data?.ipa_name || "");
    setUserIds(data?.assotiate_users?.map((u) => u.user_id) || []);
  }, [data]);


  useEffect(() => {
    let mounted = true;

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await fetchAllUsers(
          { page: 1, per_page: 1000, search_term: "" },
          account_id,
          location_id
        );
        if (mounted) setAllUsers(res?.data?.results || []);
      } catch (e) {
        notify("error", "Failed to load users", 0.5);
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    };

    loadUsers();
    return () => {
      mounted = false;
    };
  }, [account_id, location_id]);

  const closeDrawer = useCallback(() => {
    onClose();
    window.scrollTo(0, previousScrollPosition);
  }, [onClose, previousScrollPosition]);

  const toggleUser = useCallback((id) => {
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const selectedUsers = useMemo(() => {
    if (!allUsers?.length || !userIds?.length) return [];
    const set = new Set(userIds);
    return allUsers.filter((u) => set.has(u.id));
  }, [allUsers, userIds]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    const cleaned = ipaName.trim();
    if (!cleaned) {
      notify("error", "IPA name is required", 0.5);
      return;
    }

    setLoading(true);
    try {
      const res = await updateExIpa(data?.id, { ipa_name: cleaned, user_ids: userIds });

      if (res?.data?.status) {
        notify("success", res?.data?.message);
        await reloadData();
        closeDrawer();
      } else {
        notify("error", res?.data?.message || "Update failed", 0.5);
      }
    } catch (err) {
      notify("error", err?.response?.data?.detail || "Update failed", 0.5);
    } finally {
      setLoading(false);
    }
  };

  const renderUserOptions = () => {
    if (!allUsers?.length && !loadingUsers) {
      return <li className="p-2 docpro_fs_14">No users found</li>;
    }

    return allUsers.map((user) => (
      <li key={user.id} onClick={() => toggleUser(user.id)}>
        <div className="docpro_checkbox docpro_exipa_checkbox">
          <input type="checkbox" checked={userIds.includes(user.id)} readOnly />
          <label>
            <span className="docpro_exipa_user_label">
              {user.first_name} {user.last_name}
              {user.email ? ` (${user.email})` : ""}
            </span>
          </label>
        </div>
      </li>
    ));
  };

  return (
    <div ref={dropdownRef} className="docpro_exipa_modal_wrap docpro_exipa_full_h">
      <Drawer.Root open={true} onOpenChange={closeDrawer}>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay" />

          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4 docpro_exipa_drawer_xl">
            <Drawer.Title className="sr-only">Edit Ex IPA</Drawer.Title>
            <Drawer.Description className="sr-only">
              Update Ex IPA name and associated users
            </Drawer.Description>

            <div className="docpro_modal_wrap docpro_style_2 docpro_style_auto docpro_exipa_full_h">
              <div className="docpro_modal modal_height_100">
                <form onSubmit={handleUpdate} className="docpro_exipa_form">
                  {/* Header */}
                  <div className="docpro_exipa_modal_head">
                    <h2 className="docpro_fs_24 docpro_mb_0">Edit Ex IPA</h2>
                    <button
                      type="button"
                      className="docpro_exipa_close_btn"
                      onClick={closeDrawer}
                      aria-label="Close"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="docpro_exipa_body_scroll">
                    <Spacing lg="25" sm="20" />

                    <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_1">
                      <div className="docpro_grid_item">
                        <label className="docpro_fw_500 docpro_primary_color">IPA Name</label>
                        <input
                          type="text"
                          className="docpro_form_field size_lg"
                          value={ipaName}
                          onChange={(e) => setIpaName(e.target.value)}
                          placeholder="Enter IPA Name"
                        />
                      </div>
                    </div>
                    <label className="docpro_fw_500 docpro_primary_color"> Associate Users ({selectedUsers.length})</label>

                    <MultiSelectWithSearch
                      placeholder={loadingUsers ? "Loading users..." : "Search and Associate Users"}
                      showSearch={true}
                      isErrorState={false}
                    >
                      {renderUserOptions()}
                    </MultiSelectWithSearch>

                    <Spacing lg="16" sm="16" />

                    <div className="">
                      <div className="docpro_table_responsive custom-scroll docpro_exipa_table_scroll">
                        <table className="docpro_mb_0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th className="docpro_text_center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedUsers.length ? (
                              selectedUsers.map((u) => (
                                <tr key={u.id}>
                                  <td>
                                    {u.first_name} {u.last_name}
                                  </td>
                                  <td>{u.email || ""}</td>
                                  <td className="docpro_text_center">
                                    <button
                                      type="button"
                                      className="docpro_icon_btn last_btn_tip"
                                      onClick={() => toggleUser(u.id)}
                                    >
                                      <Trash2 width={18} height={18} />
                                      <span className="docpro_tip">Remove</span>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="3" className="no_data docpro_p_0 docpro_exipa_empty_row">
                                  No users associated
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>


                    <Spacing lg="30" sm="20" />
                  </div>

                  {/* Footer */}
                  <div className="docpro_modal_btns small_buttons">
                    <button
                      type="button"
                      className="docpro_bordered_btn docpro_fw_600 size_lg"
                      onClick={closeDrawer}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={loading}
                      className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg docpro_btn_green"
                    >
                      <Save width={20} height={20} />
                      <span>Update Ex IPA</span>
                      {loading && (
                        <span className="docpro_loading">
                          <Loader width={20} height={20} />
                        </span>
                      )}
                    </button>
                  </div>
                </form>

                {loading && (
                  <div className="docpro_exipa_loader_overlay">
                    <span className="docpro_loading docpro_exipa_loader_icon">
                      <Loader width={46} height={46} className="docpro_exipa_loader_svg" />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
